import { css, cssManager } from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';

export const viewerStyles = [
  themeDefaultStyles,
  cssManager.defaultStyles,
  css`
    :host {
      display: block;
      width: 100%;
      height: 600px;
      position: relative;
      font-family: var(--dees-font-family);
      contain: layout style;
      /* scroll-edge fades (dark defaults, bright overrides below) */
      --pdf-fade-strong: hsl(0 0% 0% / 0.4);
      --pdf-fade-soft: hsl(0 0% 0% / 0.12);
    }

    :host([gobright]) {
      --pdf-fade-strong: hsl(0 0% 0% / 0.08);
      --pdf-fade-soft: hsl(0 0% 0% / 0.03);
    }

    dees-tile {
      height: 100%;
    }

    .viewer-container::before,
    .viewer-container::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      height: 8px;
      z-index: 5;
      pointer-events: none;
    }

    .viewer-container::before {
      top: 0;
      background: linear-gradient(
        to bottom,
        var(--pdf-fade-strong),
        var(--pdf-fade-soft),
        transparent
      );
    }

    .viewer-container::after {
      bottom: 0;
      background: linear-gradient(
        to top,
        var(--pdf-fade-strong),
        var(--pdf-fade-soft),
        transparent
      );
    }

    .page-info {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 0 8px;
      font-size: 14px;
      color: var(--dees-color-text-secondary);
    }

    .page-input {
      width: 48px;
      height: 28px;
      border-radius: 4px;
      border: 1px solid var(--dees-color-border-strong);
      background: var(--dees-color-bg-primary);
      color: var(--dees-color-text-primary);
      text-align: center;
      font-size: 14px;
      font-family: inherit;
      outline: none;
    }

    .page-input:focus {
      border-color: var(--dees-color-link);
    }

    .page-separator {
      color: var(--dees-color-text-muted);
    }

    .zoom-level {
      font-size: 13px;
      font-weight: 500;
      min-width: 48px;
      text-align: center;
    }

    [hidden] { display: none !important; }
    .gallery-mode .sidebar { position: absolute; inset: 0; width: 100%; z-index: 2; background: var(--dees-color-bg-primary); border: 0; }
    .gallery-mode .viewer-main { visibility: hidden; }
    .gallery-mode .sidebar-content { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(180px, 100%), 1fr)); gap: 24px; align-content: start; align-items: start; padding: 24px; }
    .gallery-mode .thumbnail { margin: 0; }
    .viewer-container {
      position: absolute;
      inset: 0;
      display: flex;
      overflow: hidden;
    }

    .sidebar {
      width: 200px;
      background: transparent;
      border-right: 1px solid var(--dees-color-border-default);
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
      order: 0;
    }

    .sidebar-right .sidebar {
      border-right: none;
      border-left: 1px solid var(--dees-color-border-default);
      order: 1;
    }

    .sidebar-header {
      height: 40px;
      padding: 0 12px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid var(--dees-color-border-default);
      font-size: 13px;
      font-weight: 600;
      color: var(--dees-color-text-secondary);
    }

    .sidebar-close {
      width: 24px;
      height: 24px;
      border-radius: 4px;
      background: transparent;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--dees-color-text-secondary);
      transition: background 0.15s ease;
    }

    .sidebar-close:hover {
      background: var(--dees-color-badge-default-bg);
    }

    .sidebar-close dees-icon {
      font-size: 14px;
    }

    .sidebar-content {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 12px;
      display: block;
      overscroll-behavior: contain;
      min-height: 0;
    }

    .thumbnail {
      padding: 0;
      font: inherit;
      position: relative;
      border-radius: 2px;
      overflow: hidden;
      cursor: pointer;
      border: 2px solid transparent;
      transition: border-color 0.15s ease;
      background: var(--dees-color-badge-default-bg);
      display: block;
      width: 100%;
      margin-bottom: 12px;
      /* Default A4 aspect ratio (297mm / 210mm ≈ 1.414) */
      min-height: calc(176px * 1.414);
    }

    .thumbnail:last-child {
      margin-bottom: 0;
    }

    .thumbnail:hover {
      border-color: var(--dees-color-border-strong);
    }

    .thumbnail.active {
      border-color: var(--dees-color-link);
    }

    .thumbnail-canvas {
      display: block;
      width: 100%;
      height: auto;
    }

    .thumbnail-number {
      position: absolute;
      bottom: 4px;
      right: 4px;
      background: rgba(0, 0, 0, 0.75);
      color: white;
      font-size: 11px;
      font-weight: 500;
      padding: 2px 6px;
      border-radius: 4px;
    }

    .viewer-main {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 20px;
      scroll-behavior: smooth;
      overscroll-behavior: contain;
      min-height: 0;
      position: relative;
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      gap: 16px;
      color: var(--dees-color-text-secondary);
    }

    .loading-spinner {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 3px solid var(--dees-color-border-strong);
      border-top-color: var(--dees-color-link);
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .loading-text {
      font-size: 14px;
      font-weight: 500;
    }

    .pages-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;
    }

    .page-wrapper {
      display: flex;
      justify-content: center;
      width: 100%;
    }

    .canvas-container {
      background: white;
      box-shadow: var(--dees-shadow-md);
      border-radius: 2px;
      overflow: hidden;
      display: inline-block;
      position: relative;
    }

    .page-canvas {
      display: block;
    }

    /* Text layer for selection */
    .text-layer {
      position: absolute;
      inset: 0;
      overflow: visible;
      line-height: 1;
      text-size-adjust: none;
      forced-color-adjust: none;
      transform-origin: 0 0;
      z-index: 1;
      user-select: text;
      -webkit-user-select: text;
    }

    .text-layer span,
    .text-layer br {
      color: transparent;
      position: absolute;
      white-space: pre;
      cursor: text;
      transform-origin: 0% 0%;
      user-select: text;
      -webkit-user-select: text;
    }

    .text-layer ::selection {
      background: rgba(0, 100, 200, 0.3);
    }

    .text-layer br::selection {
      background: transparent;
    }

    .text-layer .endOfContent {
      display: block;
      position: absolute;
      inset: 100% 0 0;
      z-index: 0;
      cursor: default;
      user-select: none;
    }

    .text-layer.selecting .endOfContent {
      top: 0;
    }

    .pdf-viewer.with-sidebar .viewer-main {
      margin-left: 0;
    }

    .pdf-footer {
      height: 28px;
      padding: 0 16px;
      display: flex;
      align-items: center;
      font-size: 11px;
      color: var(--dees-color-text-muted);
      width: 100%;
      box-sizing: border-box;
      position: relative;
    }

    .pdf-footer-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .pdf-footer-left .pdf-footer-item + .pdf-footer-item {
      padding-left: 12px;
      border-left: 1px solid var(--dees-color-border-default);
    }

    .pdf-footer-center {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      font-weight: 500;
      transition: margin-left 0.15s ease;
    }

    .pdf-footer-right {
      margin-left: auto;
    }

    .pdf-footer-item {
      white-space: nowrap;
    }

    .pdf-footer-filename {
      font-family: 'Intel One Mono', 'Geist Mono', monospace;
      opacity: 0.7;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `,
];
